import React, { useState, useMemo } from 'react';
import { useActions, useCards, useDeleteAction } from '../../utils/reactQueryHooks';
import Action from './Action';
import '../../styles/actions.css';
import ActionModal from './ActionModal';
import PropTypes from 'prop-types';
import DeleteConfirmModal from '../common/DeleteConfirmModal';

/**
 * ZoneActionsBoard - Displays action cards filtered for a specific sensor
 * Similar to ActionsBoard but scoped to a single sensor
 */
const ZoneActionsBoard = ({ sensorMacAddress }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [actionDeleteMac, setActionDeleteMac] = useState(null);
  const deleteActionMutation = useDeleteAction();

  // Fetch actions and cards data
  const { data: allActions = [] } = useActions();
  const { data: cards = [] } = useCards();

  // Filter only actions associated with the current sensor
  const sensorActions = useMemo(() => {
    return allActions.filter(action => action.sensor === sensorMacAddress);
  }, [allActions, sensorMacAddress]);

  // Get the current sensor card for display purposes
  const sensorCard = useMemo(() => {
    return cards.find(card => card.macAddress === sensorMacAddress);
  }, [cards, sensorMacAddress]);

  const pendingActionDelete = useMemo(
    () => sensorActions.find((a) => a.macAddress === actionDeleteMac) ?? null,
    [sensorActions, actionDeleteMac]
  );

  // Open/close action modal
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleRequestDeleteAction = (macAddressToDelete) => {
    setActionDeleteMac(macAddressToDelete);
  };

  const handleCancelDeleteAction = () => {
    setActionDeleteMac(null);
  };

  const handleConfirmDeleteAction = () => {
    if (!actionDeleteMac) return;
    const macAddressToDelete = actionDeleteMac;
    try {
      deleteActionMutation.mutate(macAddressToDelete, {
        onSuccess: () => {
          console.log(`Action with MAC address ${macAddressToDelete} successfully deleted`);
          setActionDeleteMac(null);
        },
        onError: (error) => {
          console.error(`Error deleting action: ${error.message}`);
        }
      });
    } catch (error) {
      console.error("Error deleting action:", error);
    }
  };

  return (
    <div className="zone-actions-wrapper">
      <div className="actions-header">
        <h3 className="section-title">Zone Actions</h3>
        <button className="add-action-button green-button" onClick={openModal} title="Add new action">
          <i className="ri-add-line"></i>
        </button>
      </div>

      {sensorActions.length > 0 ? (
        <div className="sensor-actions">
          {sensorActions.map((action) => (
            <Action 
              key={action.macAddress || crypto.randomUUID()} 
              action={action} 
              onDelete={handleRequestDeleteAction} 
            />
          ))}
        </div>
      ) : (
        <div className="no-actions">
          <p>No actions available for this sensor</p>
          <button className="add-action-button green-button" onClick={openModal}>
            Add an action
          </button>
        </div>
      )}
      
      {/* Modal for adding new action */}
      {modalOpen && (
        <ActionModal 
          closeModal={closeModal} 
          cards={[sensorCard]} // Only pass the current sensor card to limit selection
        />
      )}

      <DeleteConfirmModal
        isOpen={Boolean(actionDeleteMac)}
        onClose={handleCancelDeleteAction}
        onConfirm={handleConfirmDeleteAction}
        title="Disconnect actuator?"
        message={
          pendingActionDelete?.actuatorDescription
            ? `Disconnect "${pendingActionDelete.actuatorDescription}" from this zone?`
            : 'Disconnect this actuator from this zone?'
        }
        warningText="The last paired sensor will be saved. You can reconnect it later from Device History."
        confirmLabel="Disconnect"
        isLoading={deleteActionMutation.isPending}
      />
    </div>
  );
};

ZoneActionsBoard.propTypes = {
  sensorMacAddress: PropTypes.string.isRequired
};

export default ZoneActionsBoard;
